// Read/write params in the URL query string (window.location.search).
//
// Most URL state goes through react-router's useSearchParams. These
// helpers are the escape hatch for the writes that must NOT trigger a
// re-render: panel layout (xcol/dcol), the overview pins, the pinned
// generation. They write via history.replaceState, which react-router
// never observes — it only updates its location on `popstate` and on
// its own navigate()/setSearchParams() calls. So the URL bar updates
// silently, without cascading a re-render across every useSearchParams
// consumer mid-animation (which rebuilds parent column closures,
// re-fires layout effects, and visibly flickers a FLIP transition).
//
// The silent-write property comes from replaceState, not from the URL
// shape — react-router (Browser or Hash) only resyncs on popstate and
// its own navigations. Under the old HashRouter these helpers touched
// the query string inside the hash (`#/?key=val`); now they touch the
// real query string (`/?key=val`). Same mechanism, real paths.

export function readUrlParams() {
    if (typeof window === "undefined") return new URLSearchParams();
    return new URLSearchParams(window.location.search || "");
}

export function readUrlParam(key) {
    return readUrlParams().get(key);
}

export function writeUrlParams(params) {
    if (typeof window === "undefined") return;
    const search = params.toString();
    const nextSearch = search ? `?${search}` : "";
    if (window.location.search === nextSearch) return;
    // Preserve pathname (and any hash) — only the query string is ours.
    const url = `${window.location.pathname}${nextSearch}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", url);
}

export function writeUrlParam(key, value) {
    const params = readUrlParams();
    if (value == null || value === "") params.delete(key);
    else params.set(key, value);
    writeUrlParams(params);
}

// A scope navigation carries its lens state into the SAME pushed history
// entry: focus and view replace whatever was there, and a lens's own
// parameters are set when given a value and dropped when given null or "".
export function applyScopeNavParams(next, { focus = null, view = null, params = null } = {}) {
    if (focus) next.set("focus", focus); else next.delete("focus");
    if (view) next.set("view", view); else next.delete("view");
    for (const [key, value] of Object.entries(params || {})) {
        if (value == null || value === "") next.delete(key); else next.set(key, String(value));
    }
    return next;
}
